// "use client";

import React, { ElementType, useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  StackDivider,
  SkeletonText,
  SkeletonCircle,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Link,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Icon,
  useToast,
  Alert,
  AlertIcon,
  VisuallyHidden,
  Avatar,
  useColorModeValue,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { motion, isValidMotionProp } from 'framer-motion';
import {
  FiEdit2,
  FiExternalLink,
  FiGlobe,
  FiMail,
  FiPhone,
  FiMapPin,
  FiActivity,
  FiInfo,
  FiUser,
  FiLogOut,
} from 'react-icons/fi';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { getUserProfile } from '../../hooks/users';
import { getOrganizations } from '../../services/organization';
import { Organization, User } from '../../utils/types';
import { logOut } from '../../services/account';
import { FaSignOutAlt } from 'react-icons/fa';

// Framer-friendly Chakra component
const MotionBox: any = motion(Box, {
  // @ts-expect-error - Chakra + Framer wrapper typing
  shouldForwardProp: (prop) => isValidMotionProp(prop) || prop === 'children',
});

const AccountInformationPage: React.FC = () => {
  const { orgId } = useParams();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [orgsState, setOrgsState] = useState<Organization[]>([]);
  const [user, setUser] = useState<User>();

  const cardBg = useColorModeValue('white', 'gray.800');
  const accentColor = useColorModeValue('green.500', 'green.300');

  useEffect(() => {
    getOrgsInit();
  }, []);

  const getOrgsInit = async () => {
    let response = await getOrganizations();
    console.log('orgs data', response);
    if (response.success === true && response.data) {
      setOrgsState(response.data);
    } else {
      setOrgsState([]);
    }
    return response;
  };

  const fetchAccData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getUserProfile();
      console.log('user Data', res);
      if ((res as any)?.success) {
        const data = (res as any).data as User;
        setUser(data);
      } else {
        setLoadError('Failed to load organization.');
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      setLoadError(
        'An unexpected error occurred while loading the organization.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccData();
  }, [orgId]);

  // Loading state (skeletons)
  if (loading) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <VStack spacing={4} mb={6} textAlign="center">
          <Spinner size="lg" color="green" m={15} />
          <Text color={'#006400'}>Loading account information...</Text>
        </VStack>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {loadError}
        </Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          User details not found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header with subtle motion */}
      <MotionBox
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        mb={6}
        textAlign="center"
      >
        <Avatar
          size="xl"
          name={`${user.first_name} ${user.last_name}`}
          mb={3}
          bg={accentColor}
          color="white"
          shadow="md"
        />
        <Heading size="lg" fontWeight="bold">
          {user.first_name} {user.last_name}
        </Heading>
        <Text fontSize="sm" color="gray.500">
          {user.email}
        </Text>
      </MotionBox>

      {/* Info Cards */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Card bg={cardBg} shadow="md" borderRadius="xl">
          <CardHeader>
            <HStack>
              <Icon
                as={FiInfo as ElementType}
                color={accentColor}
                boxSize={5}
              />
              <Heading size="sm">About</Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="start" spacing={4} divider={<StackDivider />}>
              <Box>
                <Text fontWeight="semibold" color="gray.600">
                  Full Name
                </Text>
                <Text>
                  {user.first_name} {user.last_name}
                </Text>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md" borderRadius="xl">
          <CardHeader>
            <HStack>
              <Icon
                as={FiUser as ElementType}
                color={accentColor}
                boxSize={5}
              />
              <Heading size="sm">Contact</Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="start" spacing={4} divider={<StackDivider />}>
              <Box>
                <Text fontWeight="semibold" color="gray.600">
                  Email
                </Text>
                <HStack color="gray.700">
                  <Icon
                    as={FiMail as ElementType}
                    boxSize={5}
                    color={accentColor}
                  />
                  <Text>{user.email || '-'}</Text>
                </HStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      <MotionBox
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        mt={8}
        textAlign="center"
      >
        <Button
          rightIcon={<Icon as={FiLogOut as ElementType} boxSize={4} />}
          colorScheme="red"
          variant="ghost"
          onClick={() => logOut()}
        >
          Log Out
        </Button>
      </MotionBox>
    </Box>
  );
};

export default AccountInformationPage;
